'use client';

import { c as _c } from "react/compiler-runtime";
import { subMonths } from 'date-fns';
import type { TFunction } from 'i18next';
import { useSearchParams } from 'next/navigation';
import type { ReactNode } from 'react';
import { Suspense, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { IntlBlock } from '@sola/ark-core/atoms/intl/Intl.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { Alert } from '@sola/ark-core/molecules/alert/alert.tsx';
import { LinkCardSkeleton } from '@sola/ark-core/molecules/link-card/link-card-skeleton.tsx';
import { LinkCard, LinkCardIcon } from '@sola/ark-core/molecules/link-card/link-card.tsx';
import { ChildImageVariantEnum, getChildImageUrlBase } from '@sola/ark-core/utils/child-image.ts';
import { useRouter } from '@sola/ark-core/utils/hooks/useRouter.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { Image } from '@sola/ark-skippy/atoms/image/image.tsx';
import { Lightbox, LightboxTrigger } from '@sola/ark-skippy/molecules/lightbox/lightbox.tsx';
import { useChildDetails } from '@sola/my-sdk/children/hooks.ts';
import { getGivingChildGiftsPath, knownMyUrls, myQueryParams } from '@sola/my-sdk/constants.ts';
import { GiftTypeEnum } from '@sola/schema/dotcom/cart/gift.ts';
import type { ChildDetails } from '@sola/schema/dotcom/children/child.ts';
import { GiftFundCodesEnum } from '@sola/schema/dotcom/shared/gifts.ts';
import { CommitmentStatusEnum } from '@sola/schema/dotcom/supporters/commitments.ts';
import type { GraphQLCommitment } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import type { BlockProps } from '@sola/schema/lib/schema.ts';
import loader from '@sola/shared-utils/ark-skippy/loader.ts';
import { getAgeFromBirthday, hasUpcomingBirthday } from '@sola/shared-utils/children/utils.ts';
import { toCalendarDateString } from '@sola/shared-utils/date.ts';
import { isCommitmentCurrent, isSponsorshipInTransition, isSponsorshipLeaving } from '@sola/shared-utils/supporters/commitments.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { useDepartureSummary, useSponsorshipsAny } from '@sola/supporter-sdk/hooks/commitments.ts';
import { useGivingHistory } from '@sola/supporter-sdk/hooks/giving-history.ts';
import { commitmentsQuery } from '@sola/supporter-sdk/queries.ts';
import { BackButton } from '../../atoms/back-button/back-button.tsx';
import { ChildBioAccordion } from '../../molecules/child-bio-accordion/child-bio-accordion.tsx';
import { DepartureAlert } from '../../molecules/departure-alert/departure-alert.tsx';
import { DepartureCta } from '../../molecules/departure-cta/departure-cta.tsx';
import { ReminderAlertBirthday } from '../../molecules/reminder-alert-birthday/reminder-alert-birthday.tsx';
import type { PageAlertConfig } from '../../types/page-alerts.ts';
import { useGiftEligibility } from '../../utils/gift-availability/use-gift-eligibility.ts';
import type { DepartureSubConfirmation } from '../../utils/sub-acceptance.ts';
import { SponsorshipsSkeleton } from '../sponsorships-list/sponsorships-skeleton.tsx';
interface ChildBioHeadingProps {
  preferredName: string;
  age: string | number;
  id: string;
}
const ChildBioHeading: React.FC<ChildBioHeadingProps> = t0 => {
  const $ = _c(11);
  const {
    preferredName,
    age,
    id
  } = t0;
  let t1;
  if ($[0] !== preferredName) {
    t1 = <h1 className="heading-md heading-neutral-amplified mb-snug-lg">{preferredName}</h1>;
    $[0] = preferredName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== age || $[3] !== id) {
    t2 = {
      age,
      id
    };
    $[2] = age;
    $[3] = id;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      ageLabel: <span className="label-potent" />,
      idLabel: <span className="label-potent" />
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2) {
    t4 = <div className="body-sm label-neutral-mild"><IntlBlock i18nKey="sponsorshipRelationship.childBio.metadata" ns="my" values={t2} components={t3} /></div>;
    $[6] = t2;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t1 || $[9] !== t4) {
    t5 = <div>{t1}{t4}</div>;
    $[8] = t1;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
};
interface ChildBioImageProps {
  child: ChildDetails;
}
const ChildBioImage: React.FC<ChildBioImageProps> = t0 => {
  const $ = _c(13);
  const {
    child
  } = t0;
  const {
    imageUrl,
    name: t1
  } = child;
  const {
    preferred: preferredName
  } = t1;
  let t2;
  if ($[0] !== imageUrl) {
    t2 = getChildImageUrlBase(imageUrl, ChildImageVariantEnum.Landscape) ?? "";
    $[0] = imageUrl;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const croppedChildImage = t2;
  let t3;
  if ($[2] !== imageUrl) {
    t3 = getChildImageUrlBase(imageUrl, ChildImageVariantEnum.Full);
    $[2] = imageUrl;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const fullChildImage = t3;
  const t4 = fullChildImage ?? imageUrl;
  let t5;
  if ($[4] !== preferredName || $[5] !== t4) {
    t5 = [{
      media: {
        image: {
          url: t4,
          alt: preferredName,
          height: 0,
          width: 0
        }
      }
    }];
    $[4] = preferredName;
    $[5] = t4;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== croppedChildImage || $[8] !== preferredName) {
    t6 = <LightboxTrigger><Image image={{
        alt: preferredName,
        url: croppedChildImage
      }} loader={loader} sizeIntent="full" aspectRatio="16-9" objectPosition="top" rounded="lg" /></LightboxTrigger>;
    $[7] = croppedChildImage;
    $[8] = preferredName;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t5 || $[11] !== t6) {
    t7 = <Lightbox key="lightbox" items={t5}>{t6}</Lightbox>;
    $[10] = t5;
    $[11] = t6;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  return t7;
};
interface ChildBioSummaryProps {
  child: ChildDetails;
}
const ChildBioSummary: React.FC<ChildBioSummaryProps> = t0 => {
  const $ = _c(9);
  const {
    child
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== child.birthDate || $[2] !== child.country || $[3] !== child.gender || $[4] !== child.name.preferred || $[5] !== t) {
    t2 = t("my:sponsorshipRelationship.childBio.summary", {
      childName: child.name.preferred,
      age: getAgeFromBirthday(child.birthDate),
      country: child.country,
      context: child.gender
    });
    $[1] = child.birthDate;
    $[2] = child.country;
    $[3] = child.gender;
    $[4] = child.name.preferred;
    $[5] = t;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <span className="body-md label-neutral-mild">{t2}</span>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
};
const getLetterCtaSubtitle = (commitment: GraphQLCommitment, t: TFunction<'my', undefined>) => {
  if (commitment.lastCorrespondenceDate) {
    return t('my:ctas.writeLetter.subtitle', {
      date: commitment.lastCorrespondenceDate
    });
  }
  return t('my:ctas.writeLetter.subtitle_fallback');
};
interface ChildBioCtaCardsProps {
  commitment: GraphQLCommitment;
}
const ChildBioFinalGiftCta: React.FC<{
  commitment: GraphQLCommitment;
}> = t0 => {
  const $ = _c(10);
  const {
    commitment
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const {
    isPending,
    isFinalGiftEligible
  } = useGiftEligibility();
  if (isPending) {
    let t2;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <div className="flex-1"><LinkCardSkeleton staticPadding={true} /></div>;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (!isFinalGiftEligible(commitment.participant?.globalId)) {
    return null;
  }
  const t2 = commitment.participant?.globalId;
  let t3;
  if ($[2] !== t2) {
    t3 = getGivingChildGiftsPath(t2, GiftTypeEnum.Final);
    $[2] = t2;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <LinkCardIcon><ArkIcon className="text-accent-default" iconName="gift-01" size="1.5rem" /></LinkCardIcon>;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t) {
    t5 = t("my:ctas.give.farewellTitle");
    $[5] = t;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== t3 || $[8] !== t5) {
    t6 = <LinkCard className="flex-1" href={t3} graphic={t4} title={t5} staticPadding={true} />;
    $[7] = t3;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  return t6;
};
const ChildBioCtaCards: React.FC<ChildBioCtaCardsProps> = t0 => {
  const $ = _c(14);
  const {
    commitment
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  let t2;
  if ($[1] !== commitment) {
    t2 = isSponsorshipLeaving(commitment) && !isSponsorshipInTransition(commitment);
    $[1] = commitment;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const isLeaving = t2;
  let t3;
  if ($[3] !== commitment || $[4] !== isLeaving || $[5] !== t) {
    t3 = commitment.canWrite && <LinkCard className="flex-1" href={t("my:ctas.writeLetter.url")} graphic={<LinkCardIcon><ArkIcon className="text-accent-default" iconName="file-heart-01" size="1.5rem" /></LinkCardIcon>} subtitle={getLetterCtaSubtitle(commitment, t)} title={t(isLeaving ? "my:ctas.writeLetter.farewellTitle" : "my:ctas.writeLetter.title")} staticPadding={true} />;
    $[3] = commitment;
    $[4] = isLeaving;
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== commitment || $[8] !== isLeaving || $[9] !== t) {
    t4 = commitment.canGiveGifts && (isLeaving ? <ChildBioFinalGiftCta commitment={commitment} /> : <LinkCard className="flex-1" href={knownMyUrls.givingChildGifts} graphic={<LinkCardIcon><ArkIcon className="text-accent-default" iconName="gift-01" size="1.5rem" /></LinkCardIcon>} title={t("my:ctas.give.title")} staticPadding={true} />);
    $[7] = commitment;
    $[8] = isLeaving;
    $[9] = t;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t3 || $[12] !== t4) {
    t5 = <div className="flex flex-col md:flex-row gap-comfy-sm w-full">{t3}{t4}</div>;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  return t5;
};
const shouldShowChildBioCtas = (commitment: GraphQLCommitment): boolean => isCommitmentCurrent(commitment) || isSponsorshipLeaving(commitment);
const isBirthdayNotificationCandidate = (commitment: GraphQLCommitment, birthDate: string) => {
  if (!isCommitmentCurrent(commitment) || isSponsorshipInTransition(commitment) || !commitment.canGiveGifts) {
    return false;
  }
  return hasUpcomingBirthday(birthDate);
};
const useBirthdayNotificationData = t0 => {
  const $ = _c(12);
  const {
    child,
    commitment
  } = t0;
  let t1;
  if ($[0] !== child || $[1] !== commitment) {
    t1 = !!child && !!commitment && isBirthdayNotificationCandidate(commitment, child.birthDate);
    $[0] = child;
    $[1] = commitment;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const isCandidate = t1;
  const {
    data,
    isFetched: commitmentsFetched,
    isError: hasCommitmentsError
  } = useSupporterQuery(commitmentsQuery);
  let t2;
  if ($[3] !== data) {
    t2 = data || [];
    $[3] = data;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const commitments = t2;
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    const end = new Date();
    const start = subMonths(end, 5);
    t3 = {
      startDate: start,
      endDate: end
    };
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const {
    startDate,
    endDate
  } = t3;
  let t4;
  let t5;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = toCalendarDateString(startDate) || "";
    t5 = toCalendarDateString(endDate) || "";
    $[6] = t4;
    $[7] = t5;
  } else {
    t4 = $[6];
    t5 = $[7];
  }
  const t6 = child?.globalId || "";
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = [GiftFundCodesEnum.Child, GiftFundCodesEnum.Birthday];
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== isCandidate || $[10] !== t6) {
    t8 = {
      start: t4,
      end: t5,
      beneficiaryGlobalId: t6,
      fundCodes: t7,
      enabled: isCandidate
    };
    $[9] = isCandidate;
    $[10] = t6;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const {
    data: givingHistoryItems,
    isError: hasGivingHistoryError,
    isFetched: givingHistoryFetched
  } = useGivingHistory(t8);
  let t9;
  bb0: {
    if (!child || !commitment || !isCandidate || !commitmentsFetched || !givingHistoryFetched || hasGivingHistoryError || hasCommitmentsError) {
      t9 = false;
      break bb0;
    }
    const hasRecurringGift = commitments.some(comm => comm.status === CommitmentStatusEnum.Active && comm.participant?.globalId === child.globalId && comm.isRecurring && (comm.fund?.code === GiftFundCodesEnum.Child || comm.fund?.code === GiftFundCodesEnum.Birthday));
    if (hasRecurringGift) {
      t9 = false;
      break bb0;
    }
    t9 = !commitments.length || !givingHistoryItems || !givingHistoryItems.length;
  }
  return t9;
};
const useComponentData = (id, departureConfirmation) => {
  const $ = _c(32);
  const searchParams = useSearchParams();
  let t0;
  if ($[0] !== id || $[1] !== searchParams) {
    t0 = id ?? searchParams.get(myQueryParams.childBio.childGlobalId);
    $[0] = id;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const globalId = t0;
  const hasGlobalId = !!globalId;
  const {
    data: child,
    isLoading: isChildLoading,
    isFetching: isChildFetching,
    isPending: isChildPending,
    isError: isChildError
  } = useChildDetails(globalId ?? undefined);
  const {
    sponsorships,
    isLoading: isSponsorshipsLoading,
    isFetching: isSponsorshipsFetching,
    isPending: isSponsorshipsPending,
    isError: isSponsorshipsError,
    isSuccess: hasCommitmentsResult
  } = useSponsorshipsAny();
  const {
    departureSummary,
    isLoading: isDepartureLoading,
    isFetching: isDepartureFetching,
    isPending: isDeparturePending
  } = useDepartureSummary();
  let t1;
  if ($[3] !== globalId || $[4] !== sponsorships) {
    let t2;
    if ($[6] !== globalId) {
      t2 = c => c.participant?.globalId === globalId;
      $[6] = globalId;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    t1 = sponsorships.find(t2);
    $[3] = globalId;
    $[4] = sponsorships;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const commitment = t1;
  let t2;
  if ($[8] !== departureSummary?.departures || $[9] !== globalId) {
    t2 = departureSummary?.departures?.find(d => d.departedChild?.globalId === globalId);
    $[8] = departureSummary?.departures;
    $[9] = globalId;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  const departure = t2;
  const allowMissingCommitment = !!departureConfirmation;
  const isLoading = isChildLoading || isDepartureLoading || !allowMissingCommitment && isSponsorshipsLoading;
  const isFetching = isChildFetching || isDepartureFetching || !allowMissingCommitment && isSponsorshipsFetching;
  const isPending = isChildPending || isDeparturePending || !allowMissingCommitment && isSponsorshipsPending;
  const isError = isChildError || !allowMissingCommitment && isSponsorshipsError;
  const isShowingSkeletons = hasGlobalId && (isLoading || isFetching || isPending) && !isError;
  const isShowingChild = !isShowingSkeletons && !!child && (!!commitment || allowMissingCommitment);
  const isShowingError = hasGlobalId && !isShowingSkeletons && (isError || !child || !commitment && !allowMissingCommitment);
  const sponsorshipCount = sponsorships?.length;
  let t3;
  if ($[11] !== departure) {
    t3 = departure ?? {};
    $[11] = departure;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const {
    departedChild,
    sub,
    autoAcceptDate
  } = t3;
  const t4 = isSponsorshipsLoading || isSponsorshipsFetching || isSponsorshipsPending;
  let t5;
  if ($[13] !== autoAcceptDate || $[14] !== departedChild || $[15] !== departure || $[16] !== sub) {
    t5 = departure ? {
      departedChild,
      sub,
      autoAcceptDate
    } : undefined;
    $[13] = autoAcceptDate;
    $[14] = departedChild;
    $[15] = departure;
    $[16] = sub;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  let t6;
  if ($[18] !== child || $[19] !== commitment || $[20] !== globalId || $[21] !== hasCommitmentsResult || $[22] !== hasGlobalId || $[23] !== isShowingChild || $[24] !== isShowingError || $[25] !== isShowingSkeletons || $[26] !== isSponsorshipsError || $[27] !== sponsorshipCount || $[28] !== sponsorships || $[29] !== t4 || $[30] !== t5) {
    t6 = {
      globalId,
      hasGlobalId,
      child,
      commitment,
      sponsorships,
      sponsorshipCount,
      hasCommitmentsResult,
      hasCommitmentsError: isSponsorshipsError,
      isCommitmentsLoading: t4,
      isShowingSkeletons,
      isShowingChild,
      isShowingError,
      departure: t5
    };
    $[18] = child;
    $[19] = commitment;
    $[20] = globalId;
    $[21] = hasCommitmentsResult;
    $[22] = hasGlobalId;
    $[23] = isShowingChild;
    $[24] = isShowingError;
    $[25] = isShowingSkeletons;
    $[26] = isSponsorshipsError;
    $[27] = sponsorshipCount;
    $[28] = sponsorships;
    $[29] = t4;
    $[30] = t5;
    $[31] = t6;
  } else {
    t6 = $[31];
  }
  return t6;
};
const DepartureSubAcceptedAlert: React.FC<{
  acceptedChildGlobalId: string;
  sponsorships?: GraphQLCommitment[];
  hasCommitmentsResult: boolean;
  hasCommitmentsError: boolean;
  isCommitmentsLoading: boolean;
}> = t0 => {
  const $ = _c(19);
  const {
    acceptedChildGlobalId,
    sponsorships,
    hasCommitmentsResult,
    hasCommitmentsError,
    isCommitmentsLoading
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["my"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  const acceptedChildQuery = useChildDetails(acceptedChildGlobalId);
  const acceptedChildName = acceptedChildQuery.data?.name.preferred || acceptedChildQuery.data?.name.first;
  const isAcceptedChildInCommitments = sponsorships?.some(sponsorship => sponsorship.participant?.globalId === acceptedChildGlobalId);
  const shouldShowSetupWarning = (hasCommitmentsResult || hasCommitmentsError) && !isAcceptedChildInCommitments;
  const t2 = `departure-sub-accepted-${acceptedChildGlobalId}`;
  const t3 = shouldShowSetupWarning ? "warning" : "positive";
  let t4;
  if ($[1] !== acceptedChildName || $[2] !== t) {
    t4 = t("my:sponsorshipRelationship.childBio.subAccepted.title", {
      childName: acceptedChildName || t("my:sponsorshipRelationship.childBio.subAccepted.fallbackName")
    });
    $[1] = acceptedChildName;
    $[2] = t;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const t5 = shouldShowSetupWarning ? "my:sponsorshipRelationship.childBio.subAccepted.setupInProgress" : "my:sponsorshipRelationship.childBio.subAccepted.message";
  let t6;
  if ($[4] !== t || $[5] !== t5) {
    t6 = t(t5);
    $[4] = t;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== t2 || $[8] !== t3 || $[9] !== t4 || $[10] !== t6) {
    t7 = {
      id: t2,
      priority: 200,
      variant: t3,
      closable: true,
      title: t4,
      message: t6
    };
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const alert = t7;
  if (isCommitmentsLoading && !hasCommitmentsResult) {
    let t8;
    if ($[12] !== t) {
      t8 = t("my:sponsorshipRelationship.childBio.subAccepted.loading");
      $[12] = t;
      $[13] = t8;
    } else {
      t8 = $[13];
    }
    let t9;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <Alert noIcon={true} title={<Skeleton height={20} width="70%" />} message={<Skeleton count={2} height={20} />} />;
      $[14] = t9;
    } else {
      t9 = $[14];
    }
    let t10;
    if ($[15] !== t8) {
      t10 = <div aria-busy="true" aria-label={t8}>{t9}</div>;
      $[15] = t8;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    return t10;
  }
  let t8;
  if ($[17] !== alert) {
    t8 = <Alert {...alert} />;
    $[17] = alert;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  return t8;
};
type ChildBioProps = BlockProps & {
  id?: string;
  departureConfirmation?: DepartureSubConfirmation | null;
};
const ChildBioContent: React.FC<ChildBioProps> = ({
  id,
  departureConfirmation
}) => {
  const {
    t
  } = useTranslation(['my']);
  const {
    usePathname
  } = useRouter();
  // eslint-disable-next-line react-compiler/react-compiler -- usePathname is a framework-agnostic hook passed via RouterContext
  const pathname = usePathname();
  const backButtonUrl = pathname.replace(/\/$/, '').replace(/\/[^/]+$/, '');
  const {
    globalId,
    child,
    commitment,
    sponsorships,
    hasCommitmentsResult,
    hasCommitmentsError,
    isCommitmentsLoading,
    sponsorshipCount,
    isShowingSkeletons,
    isShowingChild,
    isShowingError,
    departure
  } = useComponentData(id, departureConfirmation);
  const shouldShowBirthdayNotification = useBirthdayNotificationData({
    child,
    commitment
  });
  if (isShowingSkeletons) {
    return <SponsorshipsSkeleton />;
  }
  let departureAlert: ReactNode = null;
  if (departureConfirmation) {
    departureAlert = <DepartureSubAcceptedAlert acceptedChildGlobalId={departureConfirmation.acceptedChildGlobalId} sponsorships={sponsorships} hasCommitmentsResult={hasCommitmentsResult} hasCommitmentsError={hasCommitmentsError} isCommitmentsLoading={isCommitmentsLoading} />;
  } else if (globalId && departureConfirmation === null) {
    departureAlert = <DepartureAlert globalId={globalId} commitment={commitment} />;
  }
  return <section className="flex flex-col gap-comfy-lg component--container component relative max-w-md">
      {sponsorshipCount > 1 && backButtonUrl && <BackButton url={backButtonUrl} label={t('my:sponsorshipRelationship.childBio.backButton.text')} />}

      {departureAlert}

      {isShowingChild && child && <div className="flex flex-col gap-comfy-xl">
          <ChildBioHeading preferredName={child.name.preferred} age={getAgeFromBirthday(child.birthDate)} id={child.localId} />

          {departure && commitment && <DepartureCta departedChild={departure.departedChild} departedCommitment={commitment} offeredSub={departure.sub ?? undefined} autoAcceptDate={departure.autoAcceptDate} showChildDetailsCta={false} />}

          <ChildBioImage child={child} />

          <ChildBioSummary child={child} />
          {commitment?.participant && shouldShowBirthdayNotification && <ReminderAlertBirthday ctaHref={getGivingChildGiftsPath(child.globalId, GiftTypeEnum.Birthday)} participant={commitment.participant} />}

          {commitment && shouldShowChildBioCtas(commitment) && <ChildBioCtaCards commitment={commitment} />}
          <ChildBioAccordion child={child} commitment={commitment} />
        </div>}

      {isShowingError && <Alert variant="negative" title={t('my:sponsorshipRelationship.childBio.alert.title')} message={t('my:sponsorshipRelationship.childBio.alert.message')} action={<a href={t('my:ctas.contactSupport.url')}>
              {t('my:ctas.contactSupport.text')}
            </a>} />}
    </section>;
};
export const ChildBio: React.FC<ChildBioProps> = t0 => {
  const $ = _c(4);
  const {
    id,
    departureConfirmation
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <SponsorshipsSkeleton />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== departureConfirmation || $[2] !== id) {
    t2 = <Suspense fallback={t1}><ChildBioContent id={id} departureConfirmation={departureConfirmation} /></Suspense>;
    $[1] = departureConfirmation;
    $[2] = id;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  return t2;
};